import { Link } from "@mui/material";
import { useOpenUrl } from "@src/mobile/utils/openUrl";
import { Fragment, useMemo } from "react";

import { Text } from "../../components/Text";
import { detectMessageLinks } from "../utils/detectMessageLinks";

interface ChatMessageBodyProps {
  /** The (already parsed) message text to render. */
  text: string;
  /**
   * Incoming messages render on a light bubble with dark text; outgoing
   * messages render on the brand-colored bubble with inverted text. This
   * drives both the text color and the link color.
   */
  isIncoming: boolean;
}

/**
 * Renders a chat message body, turning any URLs in the text into clickable
 * links. Newlines are preserved (`whiteSpace: pre-wrap`) to match the rest of
 * the message bubble.
 *
 * Links open via {@link useOpenUrl} so they respect the Capacitor-mobile vs web
 * split (in-app browser on native, new tab on web) instead of calling
 * `window.open` directly.
 */
export function ChatMessageBody(props: ChatMessageBodyProps) {
  const { text, isIncoming } = props;
  const openUrl = useOpenUrl();
  // Memoized so we don't re-parse on every re-render (the message log re-renders
  // frequently, e.g. on read-status updates, across many messages at once).
  const segments = useMemo(() => detectMessageLinks(text), [text]);

  // Stable per-segment keys derived from each segment's offset in the string.
  // Segments partition `text`, so offsets are unique and never reorder.
  let offset = 0;

  return (
    <Text
      variant="body2"
      sx={(theme) => ({
        color: isIncoming ? theme.palette.base?.strong : theme.palette.base?.inverted?.strong,
        whiteSpace: "pre-wrap",
      })}
    >
      {segments.map((segment) => {
        const key = `${offset}-${segment.type}`;
        offset += segment.content.length;

        if (segment.type === "link") {
          return (
            <Link
              key={key}
              href={segment.href}
              target="_blank"
              rel="noopener noreferrer"
              underline="always"
              // The bubble masks message TEXT from Datadog RUM, but an `href`
              // attribute is recorded verbatim. A user-sent URL can carry PHI in
              // its path/query, so hide the link (and its href) from replay.
              data-dd-privacy="hidden"
              onClick={(event) => {
                // Let the browser handle modified clicks (cmd/ctrl/shift-click,
                // middle-click) natively so "open in new tab/window" still works.
                if (
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey ||
                  event.button !== 0
                ) {
                  return;
                }

                // For a plain click, open through the platform-aware helper rather
                // than the anchor so native uses the in-app browser. The href is
                // kept for hover/right-click/keyboard semantics.
                event.preventDefault();
                void openUrl(segment.href);
              }}
              sx={(theme) => {
                // Incoming (light bubble): the link token, which reads clearly
                // against the light background. Outgoing (dark/brand bubble): the
                // inverted near-white text color, since the link token has too
                // little contrast on the dark bubble.
                const linkColor = isIncoming
                  ? theme.palette.link?.base
                  : theme.palette.base?.inverted?.strong;

                return {
                  fontWeight: theme.typography.fontWeightMedium,
                  wordBreak: "break-word",
                  // A global app rule (`body a:not(.MuiButtonBase-root)`) paints
                  // every anchor with the brand color via BOTH `color` and
                  // `-webkit-text-fill-color`. The doubled `&&` selector bumps
                  // specificity above it so chat links use the bubble-appropriate
                  // color. `WebkitTextFillColor` and `textDecorationColor` must be
                  // set explicitly — neither follows `color`.
                  "&&": {
                    color: linkColor,
                    WebkitTextFillColor: linkColor,
                    textDecorationColor: linkColor,
                  },
                };
              }}
            >
              {segment.content}
            </Link>
          );
        }

        return <Fragment key={key}>{segment.content}</Fragment>;
      })}
    </Text>
  );
}
